import { useSearchParams } from 'next/navigation'
import { useEffect, useRef } from 'react'

import { useGuestUserUpdateApi } from '@/app/book/api/useGuestUserUpdateApi'
import useSessionStore from '@/stores/useSessionStore'

/**
 * Ports a guest session onto this app when arriving with a guest user id —
 * either the `guest_id` query param (TAC/cruises-next checkout handoff, Braze
 * label emails) or an explicitly supplied id (order-tracking arrives with
 * `id=<user_id>` and only knows it's a guest after the partial-user lookup).
 *
 * cruises-next shares its session via the same-origin `session-store`
 * localStorage key, but that can't cross origins (local dev) and doesn't cover
 * direct email arrivals. When the session is empty and a guest id is present,
 * exchange it for the guest's auth token via the existing updateGuestUser
 * mutation (its guest_user_id branch returns the guest's authToken) and
 * populate the session store. Downstream, `isGuestUser`/`mobileVerified` then
 * drive the verify-before-print flow, and verification success persists to the
 * backend through the token — without it, Rails print_all keeps bouncing the
 * still-unverified user back to order-tracking.
 *
 * No-op when a token already exists (localStorage fast path won) or when no
 * guest id is available. Fires at most once per mount; failures are silent —
 * the page still renders and printing falls back to current behavior.
 */
export function useGuestSessionBootstrap(explicitGuestId?: string | null): void {
  const searchParams = useSearchParams()
  const guestId = explicitGuestId ?? searchParams?.get('guest_id') ?? ''
  const hasHydrated = useSessionStore((state) => state.hasHydrated)
  const authToken = useSessionStore((state) => state.authToken)
  const userId = useSessionStore((state) => state.userId)
  const userRoles = useSessionStore((state) => state.userRoles)
  const setAuth = useSessionStore((state) => state.setAuth)
  const { mutateAsync: updateGuestUser } = useGuestUserUpdateApi()
  const attemptedRef = useRef<string | null>(null)

  useEffect(() => {
    if (!hasHydrated || !guestId || attemptedRef.current === guestId) return

    // Session already scoped to this order's guest — nothing to do.
    if (authToken && userId === guestId) return

    // Never clobber a real (non-guest) login. Only a STALE guest session from
    // a previous booking gets replaced — otherwise verification persists to
    // the old guest while Rails checks this order's user, and the verify
    // modal re-appears forever.
    if (authToken && !userRoles.includes('guest_user')) return

    attemptedRef.current = guestId

    updateGuestUser({ guestUserId: guestId })
      .then((response) => {
        if (!response.authToken || !response.user) return
        setAuth(
          response.authToken,
          response.user.id ?? guestId,
          response.user.email ?? null,
          ['guest_user'],
          response.user.mobileVerified ?? false,
        )
      })
      .catch(() => {
        // Silent: without a session the print button falls back to the
        // pre-existing Rails-side behavior.
      })
  }, [hasHydrated, authToken, userId, userRoles, guestId, updateGuestUser, setAuth])
}
